type VIN_ERROR_CODES = 'VIN_INVALID_LENGTH' | 'VIN_FORBIDDEN_CHARACTERS';

interface VinError {
  code: VIN_ERROR_CODES;
}

interface VinErrorLength extends VinError {
  length: number;
}

interface VinErrorForbiddenCharacters extends VinError {
  characters: string[];
}

type VinErrorType = VinErrorLength | VinErrorForbiddenCharacters;

const VIN_LENGTH = 17;
const INVALID_CHARACTERS_REGEX = /(I|i|O|o)/g;
const NON_ALPHANUMERIC_CHARACTERS_REGEX = /[^0-9a-zA-Z]/g;

export interface VinValidationResult {
  isValid: boolean;
  error: VinErrorLength | VinErrorForbiddenCharacters | null;
}

export const isVinLengthError = (error: VinErrorType): error is VinErrorLength => {
  return error.code === 'VIN_INVALID_LENGTH';
};

export const isVinForbiddenCharactersError = (error: VinErrorType): error is VinErrorForbiddenCharacters => {
  return error.code === 'VIN_FORBIDDEN_CHARACTERS';
};

export const validateScanVin = (value: string): string => {
  if (value && value.length === VIN_LENGTH + 1 && (value[0] === 'i' || value[0] === 'I')) {
    // Manage weird scenarios of some VINS starting with I's
    return value.substring(1, VIN_LENGTH + 1);
  }
  if (value && value.length >= VIN_LENGTH) {
    // Return substring to manage QR codes with the VIN followed by more info. RPO codes.
    return value.substring(0, VIN_LENGTH);
  }
  return null;
};

/**
 * @param vin the vin
 * @param isSubmission whether the submission specific logic should be used
 */
export const validateVin = (vin: string, isSubmission = false): VinValidationResult => {
  if (
    (isSubmission && (!vin || vin.trim().length !== VIN_LENGTH)) ||
    (!isSubmission && vin.trim().length > VIN_LENGTH)
  ) {
    return {
      isValid: false,
      error: {
        code: 'VIN_INVALID_LENGTH',
        length: VIN_LENGTH,
      },
    };
  }

  let invalidCharacters: string[] | null =
    vin.match(INVALID_CHARACTERS_REGEX) || vin.match(NON_ALPHANUMERIC_CHARACTERS_REGEX);
  const invalidCharactersLength: number = invalidCharacters ? invalidCharacters.length : 0;

  if (invalidCharactersLength) {
    invalidCharacters = [...new Set(invalidCharacters)]; //remove duplicates

    return {
      isValid: false,
      error: {
        code: 'VIN_FORBIDDEN_CHARACTERS',
        characters: [...invalidCharacters],
      },
    };
  }

  return {
    isValid: true,
    error: null,
  };
};
